import { supabase } from './supabase';

type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error';

type ClientLogEvent = {
  type: string;
  level?: ClientLogLevel;
  message?: string;
  error?: unknown;
  context?: Record<string, unknown>;
  tags?: Record<string, string | number | boolean | null>;
  traceId?: string;
  spanName?: string;
  parentSpanId?: string;
};

const SESSION_ID_KEY = 'shoutout_observability_session_id';
const TRACE_ID_KEY = 'shoutout_observability_trace_id';
const MAX_STRING_LENGTH = 2_000;
const DUPLICATE_WINDOW_MS = 30_000;
const SENSITIVE_KEY = /(password|passcode|otp|token|authorization|apikey|api_key|secret|card|cvv|cvc|ssn|social_security|email|phone)/i;
const SUPABASE_LOG_FUNCTION = 'log-client-event';
const IMPORTANT_FUNCTIONS: Record<string, true> = {
  'lunarpay-intention': true,
  'wasabi-presign': true,
  'video-upload': true,
  'fortis-refunds': true,
  'veriff-create-session': true,
  'signnow-create-w9-invite': true,
};
const IMPORTANT_LUNARPAY_ACTIONS: Record<string, true> = {
  'add-funds': true,
  'charge': true,
  'create-order': true,
  'create-payment-schedule': true,
  'create-subscription': true,
  'deduct-credits': true,
  'insert-donation-message': true,
  'process-payment': true,
  'save-card': true,
  'send-support-message': true,
  'update-order': true,
};
const recentEventAt = new Map<string, number>();
let installed = false;
let consolePatched = false;
let functionClientPatched = false;

function randomHex(bytes: number): string {
  const values = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(values);
  } else {
    for (let index = 0; index < values.length; index += 1) {
      values[index] = Math.floor(Math.random() * 256);
    }
  }

  return Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
}

function getSessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;

    const id = typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.sessionStorage.setItem(SESSION_ID_KEY, id);
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function getTraceId(): string {
  try {
    const existing = window.sessionStorage.getItem(TRACE_ID_KEY);
    if (existing) return existing;

    const traceId = randomHex(16);
    window.sessionStorage.setItem(TRACE_ID_KEY, traceId);
    return traceId;
  } catch {
    return randomHex(16);
  }
}

function safeString(value: unknown, maxLength = MAX_STRING_LENGTH): string | undefined {
  if (typeof value !== 'string') return undefined;
  if (!value) return undefined;
  return value.length > maxLength ? value.slice(0, maxLength) : value;
}

function sanitizeClientValue(value: unknown, depth = 0): unknown {
  if (value == null) return null;
  if (typeof value === 'string') return safeString(value);
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value;
  if (depth >= 4) return '[truncated]';

  if (Array.isArray(value)) {
    return value.slice(0, 10).map((item) => sanitizeClientValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const output: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value as Record<string, unknown>).slice(0, 25)) {
      if (SENSITIVE_KEY.test(key)) {
        output[key] = '[redacted]';
      } else if (key === 'stack' && typeof child === 'string') {
        output[key] = safeString(child, 6_000);
      } else {
        output[key] = sanitizeClientValue(child, depth + 1);
      }
    }
    return output;
  }

  return safeString(String(value));
}

function serializeError(error: unknown) {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: safeString(error.message),
      stack: safeString(error.stack, 6_000),
    };
  }

  if (typeof error === 'object' && error !== null) {
    return sanitizeClientValue(error);
  }

  return { message: safeString(String(error)) };
}

function getErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error) return safeString(error.message, 500);
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return safeString((error as { message?: unknown }).message, 500);
  }
  return safeString(String(error), 500);
}

function summarizeConsoleArg(value: unknown): unknown {
  if (value instanceof Error) return serializeError(value);
  if (typeof value === 'string') return safeString(value, 500);
  if (typeof value === 'number' || typeof value === 'boolean' || value == null) return value;
  return sanitizeClientValue(value);
}

function getInvokeAction(options: unknown): string | undefined {
  const body = options && typeof options === 'object'
    ? (options as { body?: unknown }).body
    : undefined;
  if (!body || typeof body !== 'object') return undefined;
  return safeString((body as { action?: unknown }).action, 120);
}

function shouldLogSuccessfulInvoke(functionName: string, action?: string): boolean {
  if (IMPORTANT_FUNCTIONS[functionName]) return true;
  return functionName === 'lunarpay-api' && !!action && !!IMPORTANT_LUNARPAY_ACTIONS[action];
}

function describeFunctionCall(functionName: string, action?: string): string {
  return action ? `${functionName}:${action}` : functionName;
}

function installConsoleLogging(): void {
  if (consolePatched || typeof window === 'undefined') return;
  consolePatched = true;

  const originalWarn = console.warn.bind(console);
  const originalError = console.error.bind(console);

  console.warn = (...args: unknown[]) => {
    originalWarn(...args);
    logClientEvent({
      type: 'browser.console_warn',
      level: 'warn',
      message: args.find((arg) => typeof arg === 'string') as string | undefined || 'Console warning',
      spanName: 'browser.console_warn',
      context: {
        args: args.slice(0, 3).map(summarizeConsoleArg),
        argument_count: args.length,
      },
    });
  };

  console.error = (...args: unknown[]) => {
    originalError(...args);
    logClientEvent({
      type: 'browser.console_error',
      level: 'error',
      message: args.find((arg) => typeof arg === 'string') as string | undefined || getErrorMessage(args[0]) || 'Console error',
      error: args.find((arg) => arg instanceof Error),
      spanName: 'browser.console_error',
      context: {
        args: args.slice(0, 3).map(summarizeConsoleArg),
        argument_count: args.length,
      },
    });
  };
}

function installSupabaseFunctionLogging(): void {
  if (functionClientPatched || typeof window === 'undefined') return;
  functionClientPatched = true;

  const functionsClient = supabase.functions as unknown as {
    invoke: (functionName: string, options?: unknown) => Promise<any>;
  };
  const originalInvoke = functionsClient.invoke.bind(supabase.functions);

  functionsClient.invoke = async (functionName: string, options?: unknown) => {
    const startedAt = Date.now();

    try {
      const action = getInvokeAction(options);
      const response = await originalInvoke(functionName, options);
      if (functionName !== SUPABASE_LOG_FUNCTION && response?.error) {
        logClientEvent({
          type: 'frontend.edge_function_error',
          level: 'error',
          message: `${describeFunctionCall(functionName, action)} returned an error`,
          error: response.error,
          spanName: `edge_function.${functionName}`,
          context: {
            function_name: functionName,
            action,
            duration_ms: Date.now() - startedAt,
            failure_kind: 'returned_error',
            error_message: getErrorMessage(response.error),
          },
          tags: {
            function_name: functionName,
            action: action || null,
            failure_kind: 'returned_error',
          },
        });
      } else if (functionName !== SUPABASE_LOG_FUNCTION && shouldLogSuccessfulInvoke(functionName, action)) {
        logClientEvent({
          type: 'frontend.edge_function_success',
          level: 'info',
          message: `${describeFunctionCall(functionName, action)} succeeded`,
          spanName: `edge_function.${functionName}`,
          context: {
            function_name: functionName,
            action,
            duration_ms: Date.now() - startedAt,
          },
          tags: {
            function_name: functionName,
            action: action || null,
            outcome: 'success',
          },
        });
      }
      return response;
    } catch (error) {
      const action = getInvokeAction(options);
      if (functionName !== SUPABASE_LOG_FUNCTION) {
        logClientEvent({
          type: 'frontend.edge_function_exception',
          level: 'error',
          message: `${describeFunctionCall(functionName, action)} request failed`,
          error,
          spanName: `edge_function.${functionName}`,
          context: {
            function_name: functionName,
            action,
            duration_ms: Date.now() - startedAt,
            failure_kind: 'exception',
            error_message: getErrorMessage(error),
          },
          tags: {
            function_name: functionName,
            action: action || null,
            failure_kind: 'exception',
          },
        });
      }
      throw error;
    }
  };
}


export function logClientEvent(event: ClientLogEvent): void {
  if (typeof window === 'undefined') return;

  const message = safeString(event.message);
  const dedupeKey = `${event.type}:${event.level || 'info'}:${message || ''}:${window.location.pathname}`;
  const now = Date.now();
  const lastSentAt = recentEventAt.get(dedupeKey) || 0;
  if (now - lastSentAt < DUPLICATE_WINDOW_MS) return;
  recentEventAt.set(dedupeKey, now);

  const payload = {
    type: event.type,
    level: event.level || 'info',
    message,
    error: event.error ? serializeError(event.error) : undefined,
    context: event.context,
    tags: event.tags,
    trace_id: event.traceId || getTraceId(),
    span_id: randomHex(8),
    parent_span_id: event.parentSpanId,
    span_name: event.spanName || event.type,
    timestamp: new Date().toISOString(),
    url: `${window.location.origin}${window.location.pathname}`,
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    user_agent: navigator.userAgent,
    session_id: getSessionId(),
    release: process.env.REACT_APP_RELEASE || process.env.REACT_APP_GIT_SHA || undefined,
  };

  void supabase.functions.invoke('log-client-event', { body: payload }).catch(() => {
    // Observability must never create user-visible failures or recursive logging.
  });
}

export function installFrontendErrorLogging(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  installConsoleLogging();
  installSupabaseFunctionLogging();

  window.addEventListener('error', (event) => {
    logClientEvent({
      type: 'browser.error',
      level: 'error',
      message: event.message,
      error: event.error || {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
      context: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logClientEvent({
      type: 'browser.unhandled_rejection',
      level: 'error',
      message: event.reason instanceof Error ? event.reason.message : 'Unhandled promise rejection',
      error: event.reason,
    });
  });
}
